import React, { useState } from 'react';
import { IHTCalculation, EstateAssets, EstateProfile } from '../../types';
import { ListChecks, Square, CheckSquare } from 'lucide-react';

interface ChecklistItem {
 id: string;
 label: string;
 detail?: string;
}

function generateChecklist(
 ihtCalc: IHTCalculation | null | undefined,
 assets: EstateAssets | undefined,
 profile: EstateProfile | undefined,
 routeKey: string | null | undefined,
): ChecklistItem[] {
 const items: ChecklistItem[] = [];
 const b = ihtCalc?.breakdown;
 const isIHT400 = routeKey === 'POST_2022_IHT400';
 const isExepted = routeKey === 'POST_2022_EXCEPTED';
 const hasTaxDue = (ihtCalc?.total_tax_due ?? 0) > 0;
 const taxAmount = ihtCalc?.total_tax_due ?? 0;
 const fmt = (n: number) => n.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });

 // === PHASE 1: Gather evidence ===

 // Property valuations
 if ((assets?.properties ?? []).length > 0) {
 const propCount = assets!.properties.length;
 items.push({
 id: 'property_vals',
 label: `Obtain date-of-death property valuation${propCount > 1 ? 's' : ''}`,
 detail: `HMRC requires professional valuations as at the date of death for ${propCount > 1 ? `all ${propCount} properties` : 'the property'}.`,
 });
 }

 // Bank balances
 if ((assets?.bank_accounts ?? []).length > 0) {
 const bankCount = assets!.bank_accounts.length;
 items.push({
 id: 'bank_balances',
 label: `Gather final balance${bankCount > 1 ? 's' : ''} from ${bankCount} bank account${bankCount > 1 ? 's' : ''}`,
 detail: 'Request date-of-death balance certificates from each bank or building society.',
 });
 }

 // Investment valuations
 if ((assets?.investments ?? []).length > 0) {
 items.push({
 id: 'investment_vals',
 label: 'Confirm investment valuations',
 detail: 'Obtain valuations for all investment holdings as at the date of death.',
 });
 }

 // Life insurance verification
 if (assets?.life_insurance?.exists === true) {
 if (assets.life_insurance.written_in_trust === true) {
 items.push({
 id: 'life_ins_trust',
 label: 'Confirm life insurance trust documentation',
 detail: 'Verify the trust deed is valid and notify the insurer. Proceeds paid to the trust are excluded from the estate.',
 });
 } else {
 const insVal = Number(assets.life_insurance.estimated_value) || 0;
 items.push({
 id: 'life_ins_value',
 label: `Confirm life insurance payout${insVal > 0 ? ' — estimated ' + fmt(insVal) : ''}`,
 detail: assets.life_insurance.written_in_trust === null || assets.life_insurance.written_in_trust === undefined
 ? 'Check whether the policy was written in trust. If not in trust, the payout forms part of the estate.'
 : 'The policy is not written in trust, so the payout forms part of the taxable estate. Confirm the exact amount with the insurer.',
 });
 }
 }

 // Foreign assets
 if (assets?.foreign_assets?.exists === true) {
 const countries = assets.foreign_assets.countries ?? [];
 const valEst = Number(assets.foreign_assets.value_estimate) || 0;
 items.push({
 id: 'foreign_assets',
 label: `Review probate requirements for foreign assets${valEst > 0 ? ' — estimated ' + fmt(valEst) : ''}`,
 detail: countries.length > 0
 ? `Obtain date-of-death valuations for all assets located in ${countries.join(', ')}. Check whether a separate grant of probate or legal proceeding is required in each country.`
 : 'Obtain date-of-death valuations for all foreign assets. Check whether a separate grant of probate or legal proceeding is required in the relevant country.',
 });
 }

 // Lifetime gifts
 if ((assets?.lifetime_gifts ?? []).length > 0 || assets?.gifts_last_7_years?.exists === true) {
 const giftCount = (assets?.lifetime_gifts ?? []).length;
 items.push({
 id: 'confirm_gifts',
 label: `Confirm ${giftCount > 0 ? giftCount + ' lifetime gift' + (giftCount > 1 ? 's' : '') : 'lifetime gifts'}`,
 detail: 'Review all gifts made within 7 years of death. Check bank statements for any undisclosed transfers.',
 });
 }

 // Spouse transfer records
 const sp = profile?.iht_details?.predeceased_spouse;
 if (sp?.exists === true && sp?.records_available !== 'full') {
 items.push({
 id: 'spouse_records',
 label: 'Obtain spouse-transfer records to confirm transferable allowance',
 detail: 'Obtain probate papers, IHT return, or estate accounts from the first spouse\'s estate. This is needed to claim the transferable nil-rate band.',
 });
 }

 // Trust-related steps
 const hasTrustInvolvement = assets?.trusts_or_life_interest?.exists === true;
 const trustsUncertain =
 !hasTrustInvolvement && assets?.trusts_or_life_interest?.unsure === true;
 if (hasTrustInvolvement) {
 items.push({
 id: 'trust_review',
 label: 'Review trust deeds and obtain trust valuations',
 detail: 'Trusts created by or benefiting the deceased may affect the IHT position. Obtain copies of trust deeds and current trust valuations. Schedule IHT418 may be required.',
 });
 } else if (trustsUncertain) {
 // Phase 1.5 — Discovery"Not sure" answer routes here as a review prompt.
 items.push({
 id: 'trust_uncertain_review',
 label: 'Confirm whether any trusts are involved',
 detail: 'Trust involvement is uncertain — check whether IHT400/IHT418 or specialist advice is required.',
 });
 }

 // Phase 1.5 — Domicile / residence review when origin signal flags the case.
 // Triggered by the ComplexityStep §7C answer, not the formal `domicile` field.
 const originOutsideUk = profile?.deceased?.origin_outside_uk;
 if (originOutsideUk === 'yes' || originOutsideUk === 'unsure') {
 items.push({
 id: 'origin_review',
 label: 'Review domicile and long-residence position',
 detail: 'Domicile/residence may need review because the deceased was originally from outside the UK or this is uncertain.',
 });
 }

 // Phase 1.5 — Spouse exemption assumption mismatch.
 // If the user said assets pass to a spouse but the surviving-spouse signal
 // is No or Not sure, exemption can't be safely assumed.
 const passedToSpouse = profile?.family?.assets_passed_to_spouse === 'yes';
 const spouseAlive = profile?.family?.current_spouse_alive;
 if (passedToSpouse && (spouseAlive === 'no' || spouseAlive === 'unsure')) {
 items.push({
 id: 'spouse_exemption_check',
 label: 'Confirm spouse exemption applies',
 detail: 'Spouse exemption may not apply unless there was a surviving spouse or civil partner at death.',
 });
 }

 // === PHASE 2: File and pay ===

 // IHT payment FIRST when tax is due (must be paid before probate)
 if (hasTaxDue) {
 items.push({
 id: 'arrange_payment',
 label: `Pay inheritance tax to HMRC — Estimated amount: ${fmt(taxAmount)}`,
 detail: 'IHT must normally be paid within 6 months of death and before probate is granted. Interest accrues on late payments. Consider whether instalment payment applies for qualifying assets (property, business interests).',
 });
 }

 // IHT schedules
 if (isIHT400) {
 const schedules: string[] = [];
 if (sp?.exists === true) schedules.push('IHT402 (transferable NRB)');
 if ((assets?.lifetime_gifts ?? []).length > 0) schedules.push('IHT403 (gifts)');
 if ((assets?.properties ?? []).length > 0) schedules.push('IHT405 (houses/land)');
 if (hasTrustInvolvement) schedules.push('IHT418 (trusts)');
 else if (trustsUncertain) schedules.push('IHT418 (trusts — confirm whether required)');

 items.push({
 id: 'iht_schedules',
 label: 'Complete IHT400 and supplementary schedules',
 detail: schedules.length > 0
 ? `Based on this estate, you are likely to need: ${schedules.join(', ')}. Check the IHT400 notes for any additional schedules.`
 : 'Fill in all required supplementary schedules as identified in the forms section above.',
 });

 items.push({
 id: 'submit_iht400',
 label: 'Submit IHT400 return to HMRC',
 detail: 'File the completed IHT400 and all supplementary schedules with HMRC. Allow time for processing before applying for probate.',
 });
 } else if (isExepted) {
 items.push({
 id: 'excepted_estate',
 label: 'Report estate values on the probate application (excepted estate)',
 detail: 'A lighter route — no full IHT400 — but not "nothing to file": you still report the estate values on the probate application (PA1P), and the usual probate timescales apply. Confirm the gross estate is within the excepted-estate threshold.',
 });
 }

 // Probate application
 const willExists = profile?.representatives?.will_exists;
 const probateForm = willExists === 'yes' ? 'PA1P' : willExists === 'no' ? 'PA1A' : 'PA1P or PA1A';
 items.push({
 id: 'apply_probate',
 label: `Apply for probate using form ${probateForm}`,
 detail: willExists === 'yes'
 ? 'Submit form PA1P (probate with a will) to the Probate Registry. You can apply online at gov.uk.'
 : willExists === 'no'
 ? 'Submit form PA1A (letters of administration, no will) to the Probate Registry.'
 : 'Submit the appropriate probate application form (PA1P with a will, or PA1A without) to the Probate Registry.',
 });

 // === PHASE 3: Settle and distribute ===

 // Debts
 if ((assets?.debts ?? []).length > 0) {
 const debtCount = (assets?.debts ?? []).length;
 items.push({
 id: 'settle_debts',
 label: `Notify creditors and settle ${debtCount} known debt${debtCount > 1 ? 's' : ''}`,
 detail: 'Place statutory notices (s.27 Trustee Act 1925) in The London Gazette and a local newspaper. Settle all known debts before distributing.',
 });
 } else {
 items.push({
 id: 'statutory_notices',
 label: 'Place statutory notices for unknown creditors',
 detail: 'Place notices under s.27 Trustee Act 1925 in The London Gazette and a local newspaper to protect against unknown claims.',
 });
 }

 // Distribution
 items.push({
 id: 'distribute',
 label: 'Distribute the estate to beneficiaries',
 detail: 'After all debts, taxes, and administration costs are settled, distribute according to the will or intestacy rules. Prepare estate accounts for beneficiaries.',
 });

 return items;
}

interface Props {
 ihtCalc?: IHTCalculation | null;
 assets?: EstateAssets;
 profile?: EstateProfile;
 routeKey?: string | null;
}

export const SubmissionChecklist: React.FC<Props> = ({ ihtCalc, assets, profile, routeKey }) => {
 const items = generateChecklist(ihtCalc, assets, profile, routeKey);
 const [checked, setChecked] = useState<Record<string, boolean>>({});

 const toggle = (id: string) => setChecked(prev => ({ ...prev, [id]: !prev[id] }));
 const doneCount = Object.values(checked).filter(Boolean).length;

 return (
 <div className="bg-white border border-[#E8E4DB] rounded overflow-hidden">
 <div className="bg-[#1A1A1A] px-6 py-4">
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-2">
 <ListChecks className="w-4 h-4 text-white" />
 <h2 className="text-xl font-bold text-white">Next steps checklist</h2>
 </div>
 <span className="text-xs font-bold text-[#AFA99F]">
 {doneCount}/{items.length} done
 </span>
 </div>
 <p className="text-[#AFA99F] text-sm mt-1">A dynamic checklist based on your estate profile.</p>
 </div>

 <div className="p-6">
 <div className="space-y-1">
 {items.map((item, idx) => {
 const isDone = checked[item.id] ?? false;
 return (
 <button
 key={item.id}
 onClick={() => toggle(item.id)}
 className={`w-full flex items-start gap-3 p-3 rounded-lg text-left transition-colors ${isDone ? 'bg-white/50' : 'hover:bg-[#FAFAF8]'}`}
 >
 <div className="mt-0.5 shrink-0">
 {isDone
 ? <CheckSquare className="w-4 h-4 text-[#3730A3]" />
 : <Square className="w-4 h-4 text-[#AFA99F]" />}
 </div>
 <div className="min-w-0">
 <p className={`text-sm font-medium ${isDone ? 'text-[#AFA99F] line-through' : 'text-[#1A1A1A]'}`}>
 <span className="text-[#AFA99F] mr-2">{idx + 1}.</span>
 {item.label}
 </p>
 {item.detail && !isDone && (
 <p className="text-[10px] text-[#AFA99F] mt-0.5 leading-relaxed">{item.detail}</p>
 )}
 </div>
 </button>
 );
 })}
 </div>

 <p className="text-[10px] text-[#AFA99F] mt-4 pt-3 border-t border-[#E8E4DB]">
 This checklist is generated from your estate details. It is not exhaustive — additional steps may apply depending on your circumstances.
 </p>
 </div>
 </div>
 );
};
